import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import './RecruiterApplicants.css';
import {
  ArrowLeft, Home, ChevronRight, ChevronLeft, Sparkles, Search, Filter,
  Download, FileText, Linkedin, Loader2, X, Users,
  GraduationCap, Globe, Building2, Upload,
  AlertCircle, LayoutList, FolderTree, UserPlus
} from 'lucide-react';
import Modal from 'react-modal';
import TrackingModal from './TrackingModal';

const ITEMS_PER_PAGE = 20;

const getDisplayStatus = (tracking) => {
  if (!tracking || Object.keys(tracking).length === 0) return { text: 'Applied', className: 'ra-status-pending' };
  if (tracking.finalStatus === 'Rejected') return { text: 'Rejected', className: 'ra-status-rejected' };
  if (tracking.finalStatus === 'Shortlisted') return { text: 'Hired', className: 'ra-status-hired' };
  if (tracking.hrRoundStatus === 'Rejected') return { text: 'Rejected At HR', className: 'ra-status-rejected' };
  if (tracking.round2Status === 'Rejected') return { text: 'Rejected At R2', className: 'ra-status-rejected' };
  if (tracking.round1Status === 'Rejected') return { text: 'Rejected At R1', className: 'ra-status-rejected' };
  if (tracking.onlineAssessmentStatus === 'Rejected') return { text: 'Rejected At Assessment', className: 'ra-status-rejected' };
  return { text: 'In Process', className: 'ra-status-process' };
};

const getFileUrl = (path) => {
  if (!path) return null;
  // 🚨 THE FIX: Do NOT split the string. Return the full signed URL with the token! 🚨
  if (path.startsWith('http')) return path; 
  return `${process.env.REACT_APP_API_BASE_URL}/${path}`;
};

const RecruiterApplicants = ({ jobId, jobRole, posterRole, onBack }) => {
  const [allApplicants, setAllApplicants] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [viewMode, setViewMode] = useState('grouped');
  const [selectedSourceGroup, setSelectedSourceGroup] = useState(null);
  const [selectedCollege, setSelectedCollege] = useState(null);

  const [isMatchModalOpen, setIsMatchModalOpen] = useState(false);
  const [jdToMatch, setJdToMatch] = useState(null);
  const [isMatching, setIsMatching] = useState(false);
  const [matchedStudents, setMatchedStudents] = useState(null);
  const fileInputRef = useRef(null);

  const [isSourceModalOpen, setIsSourceModalOpen] = useState(false);
  const [isParsing, setIsParsing] = useState(false);
  const [isSourcing, setIsSourcing] = useState(false);
  const sourceFileInputRef = useRef(null);
  const [sourceFormData, setSourceFormData] = useState({
    fullName: '', email: '', phone: '', collegeName: '', citizenship: '',
    totalExperience: '', currentCTC: '', expectedCTC: '', noticePeriod: ''
  });
  const [sourceResume, setSourceResume] = useState(null);

  const [isTrackingModalOpen, setIsTrackingModalOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);

  // 🚨 THE LOGIC 🚨
  const isCustomerLogin = !!localStorage.getItem('customerId');
  const isCustomerJob = String(posterRole || '').toLowerCase().trim() === 'customer';
  
  const showSourceButton = isCustomerLogin || !isCustomerJob;
  const showSourcedByColumn = !isCustomerLogin && !isCustomerJob;

  const fetchApplicants = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/job-postings/${jobId}/applicants`);
      const data = Array.isArray(response.data) ? response.data : [];
      setAllApplicants(data);
      if (data.length > 0 && data[0].matchScore !== undefined) {
        setMatchedStudents([...data].sort((a, b) => (b.matchScore || 0) - (a.matchScore || 0)));
      }
    } catch (err) {
      setError('Failed to load applicants.');
    } finally {
      setIsLoading(false);
    }
  }, [jobId]);

  useEffect(() => { if (jobId) fetchApplicants(); }, [jobId, fetchApplicants]);
  useEffect(() => { setCurrentPage(1); }, [searchQuery, statusFilter, selectedCollege, selectedSourceGroup, viewMode]);

  const counts = useMemo(() => {
    let collegeCount = 0, sourcedCount = 0, careersCount = 0;
    allApplicants.forEach(app => {
      if (app.source === 'College') collegeCount++;
      else if (app.source === 'Careers Page') careersCount++;
      else if (app.source?.startsWith('Sourced')) sourcedCount++;
    });
    return { collegeCount, sourcedCount, careersCount };
  }, [allApplicants]);

  const collegesList = useMemo(() => {
    if (selectedSourceGroup !== 'College') return [];
    const collegeMap = new Map();
    allApplicants.filter(app => app.source === 'College').forEach(app => {
      const existing = collegeMap.get(app.collegeName) || { name: app.collegeName, count: 0 };
      existing.count++;
      collegeMap.set(app.collegeName, existing);
    });
    return Array.from(collegeMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [allApplicants, selectedSourceGroup]);

  const students = useMemo(() => {
    const baseList = matchedStudents || allApplicants;
    if (viewMode === 'consolidated') return baseList;
    if (selectedSourceGroup === 'Sourced') return baseList.filter(a => a.source?.startsWith('Sourced'));
    if (selectedSourceGroup === 'Careers Page') return baseList.filter(a => a.source === 'Careers Page');
    if (selectedSourceGroup === 'College' && selectedCollege) return baseList.filter(a => a.collegeName === selectedCollege && a.source === 'College');
    return [];
  }, [allApplicants, matchedStudents, viewMode, selectedSourceGroup, selectedCollege]);

  const filteredStudents = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    let list = students.filter((s) => {
      if (q) {
        const matches = (s.fullName || '').toLowerCase().includes(q) ||
          (s.email || '').toLowerCase().includes(q) ||
          (s.collegeName || '').toLowerCase().includes(q) ||
          (s.phoneNumber || '').toLowerCase().includes(q);
        if (!matches) return false;
      }
      if (statusFilter !== 'All') {
        const stat = getDisplayStatus(s.tracking).text;
        if (statusFilter === 'Applied' && stat !== 'Applied') return false;
        if (statusFilter === 'In Process' && stat !== 'In Process') return false;
        if (statusFilter === 'Hired' && stat !== 'Hired') return false;
        if (statusFilter === 'Rejected' && !stat.includes('Reject')) return false;
      }
      return true;
    });
    return list;
  }, [students, searchQuery, statusFilter]);

  const totalPages = Math.ceil(filteredStudents.length / ITEMS_PER_PAGE);
  const paginatedStudents = filteredStudents.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const getPageNums = () => {
    const pages = [];
    let start = Math.max(1, currentPage - 2);
    let end = Math.min(totalPages, start + 4);
    if (end - start < 4) start = Math.max(1, end - 4);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  const schemaType = useMemo(() => {
    if (viewMode === 'consolidated') return 'ALL';
    if (selectedSourceGroup === 'Sourced') return 'SOURCED';
    if (selectedSourceGroup === 'Careers Page') return 'CAREERS';
    return 'COLLEGE';
  }, [viewMode, selectedSourceGroup]);

  const handleExportExcel = () => {
    if (filteredStudents.length === 0) return alert('No applicants to export!');
    const exportData = filteredStudents.map((s, idx) => {
      let row = {
        '#': idx + 1,
        Status: getDisplayStatus(s.tracking).text,
        Source: isCustomerLogin && s.source?.includes('Sourced') ? 'Sourced Candidate' : (s.source || 'N/A'),
        'Full Name': s.fullName || '',
        Email: s.email || '',
        Phone: s.phoneNumber || s.phone || ''
      };
      if (schemaType === 'SOURCED') {
        row = { ...row, College: s.collegeName || '', Citizenship: s.citizenship || '', Experience: s.totalExperience || '', 'Current CTC': s.currentCTC || '', 'Expected CTC': s.expectedCTC || '', 'Notice Period': s.noticePeriod || '', Resume: s.resume?.path ? getFileUrl(s.resume.path) : '' };
      } else if (schemaType === 'CAREERS') {
        row = { ...row, Citizenship: s.citizenship || '', College: s.collegeName || '', CGPA: s.currentCGPA || '', '10th %': s.tenthPercentile || '', '12th %': s.twelfthPercentile || '', 'Total Exp': s.totalExperience || '', 'Relevant Exp': s.relevantExperience || '', 'Current CTC': s.currentCTC || '', 'Expected CTC': s.expectedCTC || '', Skills: s.skills || '', 'Notice Period': s.noticePeriod || '', 'Serving Notice': s.isServingNotice ? 'Yes' : 'No', LinkedIn: s.linkedinURL || '', Resume: s.resume?.path ? getFileUrl(s.resume.path) : '' };
      } else {
        row = { ...row, College: s.collegeName || '', 'Roll No': s.rollNo || '', CGPA: s.currentCGPA || '', '10th %': s.tenthPercentile || '', '12th %': s.twelfthPercentile || '', '12th Stream': s.twelfthStream || '', Skills: s.skills || '', Experience: s.totalExperience || '0', 'Notice Period': s.noticePeriod || '', 'Currently Serving': s.isServingNotice ? 'Yes' : 'No', 'Last Working Day': s.lastWorkingDay || '', 'Open To Abroad': s.isAbroadOpportunity ? 'Yes' : 'No', 'Passport Number': s.passportNumber || '', 'Current CTC': s.currentCTC || '', 'Expected CTC': s.expectedCTC || '', LinkedIn: s.linkedinURL || '', Resume: s.resume?.path ? getFileUrl(s.resume.path) : '' };
      }
      if (matchedStudents) row['Match Score (%)'] = s.matchScore ?? 0;
      return row;
    });
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    XLSX.writeFile(XLSX.utils.book_append_sheet(XLSX.utils.book_new(), worksheet, 'Applicants'), `${jobRole || 'Applicants'}_Export_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const handleRowClick = (student) => {
    setSelectedStudent({ ...student, _id: student.application_id || student._id || student.id });
    setIsTrackingModalOpen(true);
  };

  const handleCloseTrackingModal = (updatedData) => {
    setIsTrackingModalOpen(false);
    setSelectedStudent(null);
    if (updatedData) {
      const targetId = updatedData.id || updatedData._id;
      const updateList = (list) => list.map((app) =>
        String(app.application_id || app._id || app.id) === String(targetId)
          ? { ...app, tracking: updatedData.tracking }
          : app
      );
      setAllApplicants((prev) => updateList(prev));
      if (matchedStudents) setMatchedStudents((prev) => updateList(prev));
    }
  };

  const openMatchModal = () => setIsMatchModalOpen(true);
  const closeMatchModal = () => { setIsMatchModalOpen(false); setJdToMatch(null); };
  const handleJdFileSelect = (event) => { if (event.target.files[0]) setJdToMatch(event.target.files[0]); };

  const handleRunMatch = async () => {
    if (!jdToMatch) return alert('Please upload a PDF.');
    setIsMatching(true);
    try {
      const formData = new FormData();
      formData.append('jdFile', jdToMatch);
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/job-postings/${jobId}/match`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const scoreMap = new Map();
      response.data.forEach((item) => scoreMap.set(String(item.application_id || item._id || item.id), item.matchScore));
      const mergedList = allApplicants.map((app) => {
        const score = scoreMap.get(String(app.application_id || app._id || app.id));
        return score !== undefined ? { ...app, matchScore: score } : null;
      }).filter(Boolean).sort((a, b) => b.matchScore - a.matchScore);
      setMatchedStudents(mergedList);
      closeMatchModal();
    } catch (err) {
      alert('Matching failed.');
    } finally {
      setIsMatching(false);
    }
  };

  const handleSourceResumeUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSourceResume(file);
    setIsParsing(true);
    const formData = new FormData();
    formData.append('resume', file);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/job-postings/parse-resume`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const parsed = response.data;
      setSourceFormData(prev => ({
        ...prev,
        fullName: parsed.fullName || prev.fullName,
        email: parsed.email || prev.email,
        phone: parsed.phone || prev.phone,
        collegeName: parsed.collegeName || prev.collegeName,
        citizenship: parsed.citizenship || prev.citizenship,
        totalExperience: parsed.totalExperience || prev.totalExperience,
        currentCTC: parsed.currentCTC || prev.currentCTC,
        expectedCTC: parsed.expectedCTC || prev.expectedCTC,
        noticePeriod: parsed.noticePeriod || prev.noticePeriod
      }));
      alert('Resume parsed successfully! Please verify the details below.');
    } catch (error) {
      alert('Could not auto-parse resume. Please enter details manually.');
    } finally {
      setIsParsing(false);
    }
  };

  const handleSourceFormChange = (e) => setSourceFormData({ ...sourceFormData, [e.target.name]: e.target.value });

  const handleSourceSubmit = async (e) => {
    e.preventDefault();
    setIsSourcing(true);
    const formData = new FormData();
    if (sourceResume) formData.append('resume', sourceResume);
    Object.entries(sourceFormData).forEach(([key, value]) => formData.append(key, value));
    const posterId = localStorage.getItem('userId') || localStorage.getItem('recruiterId') || localStorage.getItem('customerId');
    if (posterId) formData.append('posterId', posterId);
    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/job-postings/${jobId}/source-candidate`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      alert('Candidate sourced successfully!');
      setIsSourceModalOpen(false);
      setSourceResume(null);
      setSourceFormData({ fullName: '', email: '', phone: '', collegeName: '', citizenship: '', totalExperience: '', currentCTC: '', expectedCTC: '', noticePeriod: '' });
      fetchApplicants();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to source candidate.');
    } finally {
      setIsSourcing(false);
    }
  };

  const handleResumeDownload = async (student, e) => {
    e.stopPropagation(); 
    const resumeUrl = getFileUrl(student.resume.path);

    try {
      // Corrected keys based on your LocalStorage screenshot
      const downloaderName = localStorage.getItem('customerName') || 'Customer';
      const downloaderEmail = localStorage.getItem('email') || 'Not provided';
      
      // Using the customerId as the identifier/token
      const token = localStorage.getItem('token') || localStorage.getItem('customerId'); 

      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/admin/resume-downloads`, {
        jobName: jobRole || 'Unknown Job',
        jobLocation: 'Refer to Job Post',
        customerName: downloaderName,
        customerEmail: downloaderEmail,
        candidateName: student.fullName,
        candidateEmail: student.email || student.personalEmail,
        candidatePhone: student.phoneNumber || student.phone,
        resumeUrl: resumeUrl
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      console.log("✅ Download logged successfully!");
    } catch (err) {
      console.error("❌ Silent log failed. Backend said:", err.response?.data || err.message);
    }

    // Instantly open the PDF
    window.open(resumeUrl, '_blank', 'noopener,noreferrer');
  };

  if (isLoading) return (
    <div className="ra-root">
      <div className="ra-loading">
        <div className="ra-loading-card">
          <Loader2 className="ra-spin" size={36} />
          <span>Loading applicants…</span>
        </div>
      </div>
    </div>
  );

  if (error) return (
    <div className="ra-root">
      <div className="ra-loading">
        <div className="ra-loading-card ra-error-card">
          <AlertCircle size={40} className="ra-error-icon" />
          <span>{error}</span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="ra-root">
      <div className="ra-bg-blob ra-blob-1" />
      <div className="ra-bg-blob ra-blob-2" />
      <input type="file" ref={fileInputRef} onChange={handleJdFileSelect} style={{ display: 'none' }} accept=".pdf" />

      <button className="ra-back-btn" onClick={onBack}><ArrowLeft size={16} /> Back to All Jobs</button>

      <div className="ra-header">
        <div className="ra-header-left">
          <div className="ra-title-icon"><Users size={26} /></div>
          <div>
            <h1 className="ra-title">Applicants</h1>
            <p className="ra-subtitle">{jobRole}</p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          {showSourceButton && (
            <button className="ra-match-btn" onClick={() => setIsSourceModalOpen(true)}>
              <UserPlus size={16} /> Source Candidate
            </button>
          )}
          <span className="ra-count-badge"><Users size={14} />{allApplicants.length} total</span>
        </div>
      </div>

      <div style={{ display: 'flex', background: '#f1f5f9', padding: '4px', borderRadius: '8px', width: 'max-content', marginBottom: '20px', border: '1px solid #e2e8f0' }}>
        <button onClick={() => { setViewMode('grouped'); setSelectedSourceGroup(null); setSelectedCollege(null); }} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', borderRadius: '6px', border: 'none', cursor: 'pointer', background: viewMode === 'grouped' ? '#fff' : 'transparent', boxShadow: viewMode === 'grouped' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none', fontWeight: viewMode === 'grouped' ? 'bold' : 'normal', color: viewMode === 'grouped' ? '#0f172a' : '#64748b' }}>
          <FolderTree size={16} /> Group by Source
        </button>
        <button onClick={() => setViewMode('consolidated')} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', borderRadius: '6px', border: 'none', cursor: 'pointer', background: viewMode === 'consolidated' ? '#fff' : 'transparent', boxShadow: viewMode === 'consolidated' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none', fontWeight: viewMode === 'consolidated' ? 'bold' : 'normal', color: viewMode === 'consolidated' ? '#0f172a' : '#64748b' }}>
          <LayoutList size={16} /> Consolidated View
        </button>
      </div>

      {viewMode === 'grouped' && (
        <div className="ra-breadcrumb">
          <span className="ra-crumb" onClick={() => { setSelectedSourceGroup(null); setSelectedCollege(null); }}><Home size={13} /> All Groups</span>
          {selectedSourceGroup && (<><ChevronRight size={13} className="ra-crumb-sep" /><span className={`ra-crumb ${!selectedCollege ? 'active' : ''}`} onClick={() => setSelectedCollege(null)}>{selectedSourceGroup}</span></>)}
          {selectedCollege && (<><ChevronRight size={13} className="ra-crumb-sep" /><span className="ra-crumb active">{selectedCollege}</span></>)}
        </div>
      )}

      {viewMode === 'grouped' && !selectedSourceGroup && (
        <div className="ra-grid">
          <div className="ra-select-card" onClick={() => setSelectedSourceGroup('College')}>
            <div className="ra-select-icon"><Building2 size={20} /></div>
            <div className="ra-select-info"><span className="ra-select-title">College Applicants</span><span className="ra-select-sub"><Users size={11} /> {counts.collegeCount} applicant{counts.collegeCount !== 1 ? 's' : ''}</span></div>
            <ChevronRight size={18} className="ra-select-arrow" />
          </div>
          <div className="ra-select-card" onClick={() => setSelectedSourceGroup('Sourced')}>
            <div className="ra-select-icon" style={{color: '#be185d', background: '#fce7f3'}}><UserPlus size={20} /></div>
            <div className="ra-select-info"><span className="ra-select-title">Sourced Candidates</span><span className="ra-select-sub"><Users size={11} /> {counts.sourcedCount} applicant{counts.sourcedCount !== 1 ? 's' : ''}</span></div>
            <ChevronRight size={18} className="ra-select-arrow" />
          </div>
          <div className="ra-select-card" onClick={() => setSelectedSourceGroup('Careers Page')}>
            <div className="ra-select-icon" style={{color: '#166534', background: '#dcfce7'}}><Globe size={20} /></div>
            <div className="ra-select-info"><span className="ra-select-title">Careers Page</span><span className="ra-select-sub"><Users size={11} /> {counts.careersCount} applicant{counts.careersCount !== 1 ? 's' : ''}</span></div>
            <ChevronRight size={18} className="ra-select-arrow" />
          </div>
        </div>
      )}

      {viewMode === 'grouped' && selectedSourceGroup === 'College' && !selectedCollege && (
        <div className="ra-grid">
          {collegesList.length === 0 ? (
            <div className="ra-empty-card" style={{ gridColumn: '1 / -1' }}><Building2 size={40} className="ra-empty-icon" /><h3>No college applicants yet</h3></div>
          ) : (
            collegesList.map((college) => (
              <div key={college.name} className="ra-select-card" onClick={() => setSelectedCollege(college.name)}>
                <div className="ra-select-icon ra-icon-purple"><Building2 size={20} /></div>
                <div className="ra-select-info"><span className="ra-select-title">{college.name}</span><span className="ra-select-sub"><Users size={11} /> {college.count} applicant{college.count !== 1 ? 's' : ''}</span></div>
                <ChevronRight size={18} className="ra-select-arrow" />
              </div>
            ))
          )}
        </div>
      )}

      {((viewMode === 'consolidated') || (selectedSourceGroup === 'Sourced') || (selectedSourceGroup === 'Careers Page') || (selectedCollege)) && (
        <>
          <div className="ra-list-header">
            <div className="ra-list-title">
              <GraduationCap size={20} className="ra-list-title-icon" />
              <h3>{viewMode === 'consolidated' ? 'All Applicants' : selectedCollege ? `Applicants from ${selectedCollege}` : `${selectedSourceGroup} Applicants`}</h3>
              <span className="ra-list-count">{filteredStudents.length}</span>
            </div>
            <div className="ra-list-actions">
              <button className="ra-export-btn" onClick={handleExportExcel} disabled={filteredStudents.length === 0}><Download size={15} /> Export Excel</button>
              <button className="ra-match-btn" onClick={openMatchModal} disabled={isMatching || students.length === 0}><Sparkles size={15} />{matchedStudents ? 'Rematch Profiles' : 'Run Profile Match'}</button>
            </div>
          </div>

          <div className="ra-filters">
            <div className="ra-search-wrap">
              <Search size={16} className="ra-search-icon" />
              <input className="ra-search" type="text" placeholder="Search by name, email, phone..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
              {searchQuery && (<button className="ra-clear-btn" onClick={() => setSearchQuery('')}><X size={12} /></button>)}
            </div>
            <div className="ra-filter-group">
              <span className="ra-filter-label"><Filter size={12} /> Status</span>
              {['All', 'Applied', 'In Process', 'Hired', 'Rejected'].map((s) => (
                <button key={s} className={`ra-filter-btn ${statusFilter === s ? `active ra-fb-${s.toLowerCase().replace(' ', '')}` : ''}`} onClick={() => setStatusFilter(s)}>{s}</button>
              ))}
            </div>
          </div>

          <div className="ra-table-card">
            <div className="ra-table-wrap">
              <table className="ra-table">
                <thead>
                  <tr>
                    {matchedStudents && <th className="ra-th-score">Match</th>}
                    <th className="ra-th-status">Status</th>
                    {schemaType === 'ALL' && <th>Source</th>}
                    {schemaType === 'SOURCED' && showSourcedByColumn && <th>Sourced By</th>}
                    <th className="ra-th-name ra-sticky-col">Full Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    {(schemaType === 'SOURCED' || schemaType === 'CAREERS' || schemaType === 'ALL') && <th>College</th>}
                    {(schemaType === 'SOURCED' || schemaType === 'CAREERS') && <th>Citizenship</th>}
                    {(schemaType === 'CAREERS' || schemaType === 'COLLEGE' || schemaType === 'ALL') && <th>CGPA</th>}
                    {(schemaType === 'CAREERS' || schemaType === 'COLLEGE' || schemaType === 'ALL') && <th>10th %</th>}
                    {(schemaType === 'CAREERS' || schemaType === 'COLLEGE' || schemaType === 'ALL') && <th>12th %</th>}
                    {(schemaType === 'CAREERS') && <th>Relevant Exp</th>}
                    {(schemaType === 'COLLEGE' || schemaType === 'ALL') && <th>Roll No</th>}
                    {(schemaType === 'COLLEGE' || schemaType === 'ALL') && <th>12th Stream</th>}
                    {(schemaType === 'CAREERS' || schemaType === 'COLLEGE' || schemaType === 'ALL') && <th className="ra-th-skills">Skills</th>}
                    <th>Total Exp</th>
                    <th>Current CTC</th>
                    <th>Expected CTC</th>
                    <th>Notice Period</th>
                    {(schemaType === 'CAREERS' || schemaType === 'COLLEGE' || schemaType === 'ALL') && <th>Serving?</th>}
                    {(schemaType === 'COLLEGE' || schemaType === 'ALL') && <th>Last Day</th>}
                    {(schemaType === 'COLLEGE' || schemaType === 'ALL') && <th>Abroad?</th>}
                    {(schemaType === 'COLLEGE' || schemaType === 'ALL') && <th>Passport</th>}
                    <th>Links</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedStudents.length === 0 ? (
                    <tr><td colSpan={20} className="ra-empty-row"><Users size={32} className="ra-empty-icon" /><p>No applicants match your filters</p></td></tr>
                  ) : (
                    paginatedStudents.map((student) => {
                      const statusInfo = getDisplayStatus(student.tracking);
                      const rowKey = student.application_id || student._id || student.id || Math.random();
                      const score = student.matchScore ?? 0;
                      return (
                        <tr key={rowKey} onClick={() => handleRowClick(student)} className="ra-row">
                          {matchedStudents && (<td className="ra-td-score"><span className={`ra-match-score ${score >= 75 ? 'high' : score >= 50 ? 'mid' : 'low'}`}>{score}%</span></td>)}
                          <td><span className={`ra-status-badge ${statusInfo.className}`}>{statusInfo.text}</span></td>
                          
                          {schemaType === 'ALL' && (
                            <td>
                              <span style={{ padding: '4px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold', whiteSpace: 'nowrap', backgroundColor: student.source === 'College' ? '#e0e7ff' : student.source?.includes('Sourced') ? '#fce7f3' : '#dcfce7', color: student.source === 'College' ? '#4338ca' : student.source?.includes('Sourced') ? '#be185d' : '#166534' }}>
                                {isCustomerLogin && student.source?.includes('Sourced') ? 'Sourced Candidate' : student.source}
                              </span>
                            </td>
                          )}
                          
                          {schemaType === 'SOURCED' && showSourcedByColumn && (
                            <td className="ra-td-secondary" style={{ fontWeight: '500', color: '#be185d' }}>
                              {student.source ? student.source.replace('Sourced: ', '') : 'Unknown'}
                            </td>
                          )}
                          
                          <td className="ra-td-name ra-sticky-col"><div className="ra-name-wrap"><div className="ra-name-avatar">{(student.fullName || '?')[0].toUpperCase()}</div><span>{student.fullName}</span></div></td>
                          <td className="ra-td-secondary">{student.email || '—'}</td>
                          <td className="ra-td-secondary">{student.phoneNumber || student.phone || '—'}</td>
                          {(schemaType === 'SOURCED' || schemaType === 'CAREERS' || schemaType === 'ALL') && <td className="ra-td-secondary">{student.collegeName && student.collegeName !== 'Not Provided' ? student.collegeName : '—'}</td>}
                          {(schemaType === 'SOURCED' || schemaType === 'CAREERS') && <td className="ra-td-secondary">{student.citizenship && student.citizenship !== 'Not Provided' ? student.citizenship : '—'}</td>}
                          {(schemaType === 'CAREERS' || schemaType === 'COLLEGE' || schemaType === 'ALL') && <td>{student.currentCGPA && student.currentCGPA !== 'Not Provided' ? <span className="ra-num-badge ra-num-blue">{student.currentCGPA}</span> : <span className="ra-td-dim">—</span>}</td>}
                          {(schemaType === 'CAREERS' || schemaType === 'COLLEGE' || schemaType === 'ALL') && <td>{student.tenthPercentile && student.tenthPercentile !== 'Not Provided' ? <span className="ra-num-badge ra-num-teal">{student.tenthPercentile}</span> : <span className="ra-td-dim">—</span>}</td>}
                          {(schemaType === 'CAREERS' || schemaType === 'COLLEGE' || schemaType === 'ALL') && <td>{student.twelfthPercentile && student.twelfthPercentile !== 'Not Provided' ? <span className="ra-num-badge ra-num-purple">{student.twelfthPercentile}</span> : <span className="ra-td-dim">—</span>}</td>}
                          {(schemaType === 'CAREERS') && <td className="ra-td-secondary">{student.relevantExperience && student.relevantExperience !== 'Not Provided' ? student.relevantExperience : '—'}</td>}
                          {(schemaType === 'COLLEGE' || schemaType === 'ALL') && <td className="ra-td-mono">{student.rollNo && student.rollNo !== 'EXTERNAL' ? student.rollNo : <span className="ra-td-dim">—</span>}</td>}
                          {(schemaType === 'COLLEGE' || schemaType === 'ALL') && <td className="ra-td-secondary">{student.twelfthStream && student.twelfthStream !== 'Not Provided' ? student.twelfthStream : <span className="ra-td-dim">—</span>}</td>}
                          {(schemaType === 'CAREERS' || schemaType === 'COLLEGE' || schemaType === 'ALL') && <td className="ra-td-skills"><div className="ra-skills-wrap" title={student.skills}>{student.skills && student.skills !== 'Not Provided' ? student.skills : <span className="ra-td-dim">—</span>}</div></td>}
                          <td className="ra-td-secondary">{student.totalExperience && student.totalExperience !== 'Not Provided' ? student.totalExperience : '0'}</td>
                          <td className="ra-td-secondary">{student.currentCTC && student.currentCTC !== 'Not Provided' ? student.currentCTC : '—'}</td>
                          <td className="ra-td-secondary">{student.expectedCTC && student.expectedCTC !== 'Not Provided' ? student.expectedCTC : '—'}</td>
                          <td className="ra-td-secondary">{student.noticePeriod && student.noticePeriod !== 'Not Provided' ? student.noticePeriod : '—'}</td>
                          {(schemaType === 'CAREERS' || schemaType === 'COLLEGE' || schemaType === 'ALL') && <td><span className={`ra-yn ${student.isServingNotice ? 'yes' : 'no'}`}>{student.isServingNotice ? 'Yes' : 'No'}</span></td>}
                          {(schemaType === 'COLLEGE' || schemaType === 'ALL') && <td className="ra-td-secondary">{student.lastWorkingDay && student.lastWorkingDay !== 'Not Provided' ? student.lastWorkingDay : <span className="ra-td-dim">—</span>}</td>}
                          {(schemaType === 'COLLEGE' || schemaType === 'ALL') && <td><span className={`ra-yn ${student.isAbroadOpportunity ? 'yes' : 'no'}`}>{student.isAbroadOpportunity ? 'Yes' : 'No'}</span></td>}
                          {(schemaType === 'COLLEGE' || schemaType === 'ALL') && <td className="ra-td-secondary">{student.passportNumber && student.passportNumber !== 'Not Provided' ? student.passportNumber : <span className="ra-td-dim">—</span>}</td>}
                          <td className="ra-td-links">
                            {/* 🚨 THE REPLACED BUTTON 🚨 */}
                            {student.resume?.path && (
                              <button 
                                type="button" 
                                onClick={(e) => handleResumeDownload(student, e)} 
                                className="ra-icon-link ra-icon-pdf"
                                title="Download Resume"
                                style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 0 }}
                              >
                                <FileText size={14} />
                              </button>
                            )}
                            {schemaType !== 'SOURCED' && student.linkedinURL && student.linkedinURL !== 'Not Provided' && (<a href={student.linkedinURL} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="ra-icon-link ra-icon-li"><Linkedin size={14} /></a>)}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="ra-pagination">
                <span className="ra-page-info">Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong></span>
                <div className="ra-page-controls">
                  <button className="ra-page-btn nav" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}><ChevronLeft size={14} /></button>
                  {getPageNums().map((p) => (<button key={p} className={`ra-page-btn ${currentPage === p ? 'active' : ''}`} onClick={() => setCurrentPage(p)}>{p}</button>))}
                  <button className="ra-page-btn nav" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}><ChevronRight size={14} /></button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* ═══ SOURCE CANDIDATE MODAL ═══ */}
      <Modal
        isOpen={isSourceModalOpen}
        onRequestClose={() => setIsSourceModalOpen(false)}
        className="ra-source-modal"
        overlayClassName="ra-source-overlay"
      >
        <div className="ra-source-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#e0e7ff', display: 'grid', placeItems: 'center', color: '#4f46e5', flexShrink: 0 }}>
              <UserPlus size={20} />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '17px', fontWeight: '700', color: '#0f172a', fontFamily: 'Inter, sans-serif' }}>Source Candidate manually</h2>
              <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>Upload resume to auto-fill details</p>
            </div>
          </div>
          <button onClick={() => setIsSourceModalOpen(false)} style={{ width: '32px', height: '32px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer', display: 'grid', placeItems: 'center', color: '#64748b', flexShrink: 0 }}>
            <X size={16} />
          </button>
        </div>

        <div className="ra-source-body">
          <div
            onClick={() => sourceFileInputRef.current.click()}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '20px', background: '#f8fafc', border: '2px dashed #cbd5e1', borderRadius: '10px', cursor: 'pointer', textAlign: 'center', marginBottom: '20px', transition: 'all 0.2s' }}
            onMouseEnter={e => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.borderColor = '#94a3b8'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
          >
            <input type="file" ref={sourceFileInputRef} onChange={handleSourceResumeUpload} style={{ display: 'none' }} accept=".pdf" />
            {isParsing ? (
              <>
                <Loader2 size={22} style={{ color: '#6366f1', animation: 'ra-spin 0.9s linear infinite' }} />
                <p style={{ margin: 0, fontSize: '13px', color: '#475569', fontWeight: '500' }}>Parsing Resume Details...</p>
              </>
            ) : (
              <>
                <Upload size={22} style={{ color: '#64748b' }} />
                <p style={{ margin: 0, fontSize: '13px', color: '#475569' }}>
                  {sourceResume ? <strong style={{ color: '#4f46e5' }}>{sourceResume.name}</strong> : 'Click to upload Resume (PDF) to auto-fill'}
                </p>
              </>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            {[
              { label: 'Full Name', name: 'fullName', type: 'text', required: true },
              { label: 'Email Address', name: 'email', type: 'email', required: true },
              { label: 'Phone Number', name: 'phone', type: 'text' },
              { label: 'College / University', name: 'collegeName', type: 'text', placeholder: 'e.g. Harvard University' },
              { label: 'Citizenship', name: 'citizenship', type: 'text' },
              { label: 'Total Experience', name: 'totalExperience', type: 'text', placeholder: 'e.g. 3 Years' },
              { label: 'Current CTC', name: 'currentCTC', type: 'text' },
              { label: 'Expected CTC', name: 'expectedCTC', type: 'text' },
            ].map(({ label, name, type, required, placeholder }) => (
              <div key={name}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '5px' }}>
                  {label} {required && <span style={{ color: '#ef4444' }}>*</span>}
                </label>
                <input
                  required={required}
                  type={type}
                  name={name}
                  value={sourceFormData[name]}
                  onChange={handleSourceFormChange}
                  placeholder={placeholder || ''}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: '7px', border: '1px solid #d1d5db', fontSize: '13px', fontFamily: 'Inter, sans-serif', color: '#111827', outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.15s' }}
                  onFocus={e => e.target.style.borderColor = '#6366f1'}
                  onBlur={e => e.target.style.borderColor = '#d1d5db'}
                />
              </div>
            ))}

            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '5px' }}>Notice Period</label>
              <input
                type="text"
                name="noticePeriod"
                value={sourceFormData.noticePeriod}
                onChange={handleSourceFormChange}
                placeholder="e.g. 30 Days"
                style={{ width: '100%', padding: '8px 10px', borderRadius: '7px', border: '1px solid #d1d5db', fontSize: '13px', fontFamily: 'Inter, sans-serif', color: '#111827', outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.15s' }}
                onFocus={e => e.target.style.borderColor = '#6366f1'}
                onBlur={e => e.target.style.borderColor = '#d1d5db'}
              />
            </div>
          </div>
        </div>

        <div className="ra-source-footer">
          <button
            type="button"
            onClick={() => setIsSourceModalOpen(false)}
            style={{ padding: '9px 18px', borderRadius: '8px', border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'Inter, sans-serif' }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSourceSubmit}
            disabled={isSourcing || isParsing}
            style={{ padding: '9px 18px', borderRadius: '8px', border: 'none', background: isSourcing || isParsing ? '#a5b4fc' : '#4f46e5', color: '#fff', fontSize: '13px', fontWeight: '600', cursor: isSourcing || isParsing ? 'not-allowed' : 'pointer', fontFamily: 'Inter, sans-serif', display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            {isSourcing ? <><Loader2 size={14} style={{ animation: 'ra-spin 0.9s linear infinite' }} /> Adding…</> : 'Add Candidate'}
          </button>
        </div>
      </Modal>

      {/* ═══ MATCH MODAL ═══ */}
      <Modal isOpen={isMatchModalOpen} onRequestClose={closeMatchModal} className="ra-modal" overlayClassName="ra-overlay">
        <div className="ra-modal-header">
          <div className="ra-modal-header-left"><div className="ra-modal-icon"><Sparkles size={20} /></div><div><h2 className="ra-modal-title">Run Profile Match</h2></div></div>
          <button onClick={closeMatchModal} className="ra-modal-close"><X size={20} /></button>
        </div>
        <div className="ra-modal-body">
          <div className="ra-upload-zone" onClick={() => fileInputRef.current.click()}>
            <Upload size={28} className="ra-upload-icon" />
            <p>{jdToMatch ? <strong>{jdToMatch.name}</strong> : 'Click to choose a JD file'}</p>
          </div>
        </div>
        <div className="ra-modal-footer">
          <button className="ra-modal-secondary" onClick={closeMatchModal}>Cancel</button>
          <button className="ra-modal-primary" onClick={handleRunMatch} disabled={isMatching || !jdToMatch}>
            {isMatching ? <><Loader2 className="ra-spin" size={15} /> Matching…</> : <><Sparkles size={15} /> Start Match</>}
          </button>
        </div>
      </Modal>

      {selectedStudent && <TrackingModal isOpen={isTrackingModalOpen} onClose={handleCloseTrackingModal} student={selectedStudent} />}
    </div>
  );
};

export default RecruiterApplicants;